import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';

// Data structure for FAQs grouped by module. Feel free to extend or modify this object.
const faqModules = [
  {
    module: 'Account & Profile',
    faqs: [
      {
        question: 'How do I create an account?',
        answer: 'You can create an account using either your mobile number or Google account email. During signup, you\'ll be prompted to set a username, password, date of birth (DOB), and other basic details to complete your profile. Once your account is created, you can login using your username and password. You can create multiple accounts using the same phone number or Google email, each with a different username.'
      },
      {
        question: 'How do I login to my account?',
        answer: 'Use your username and password that you set during account creation to login to your account.'
      },
      {
        question: 'Can I change my username later?',
        answer: 'No, you cannot change your username once it is set during account creation. Please choose your username carefully as it cannot be modified later.'
      },
      {
        question: 'How do I change my password?',
        answer: 'Go to Settings → Account and Management → Change Password. Enter your current password and set a new password to update it.'
      },
      {
        question: 'I forgot my password or username what should I do?',
        answer: 'If you forgot your password or username, go to the login screen and tap "Forgot Password". Follow the reset instructions and verify your account using your mobile number or email. You\'ll be able to reset your password and retrieve your username information.'
      },
      {
        question: 'Can I have multiple accounts with the same phone number or Google email?',
        answer: 'Yes, you can create multiple accounts using the same phone number or Google account email. Each account must have a unique username, so you can have different accounts with different usernames linked to the same phone number or email address.'
      },
      {
        question: 'What does "Private Account" mean?',
        answer: 'When toggled, only people you approve of can see your posts and activity it\'s like locking your door; only invited people can come in.'
      },
      {
        question: 'What does "Sync Contacts" do?',
        answer: 'When toggled, the app checks your phone contacts to help you find friends who are already using the app. If it\'s off, we won\'t look at your contacts.'
      },
      {
        question: 'What are "Blocked Accounts"?',
        answer: 'This section lets you see and manage a list of people you\'ve blocked. Blocked users can\'t view your profile or interact with you.'
      },
      {
        question: 'What notifications can I control under Push Notifications?',
        answer: 'You can toggle on/off alerts for Likes, Comments, Shares, Direct messages, Calls, Follows, and New posts. Use the switches to decide what you want to be notified about.'
      },
      {
        question: 'What does "Archive Settings" mean?',
        answer: 'When this is on, chats you archive will stay hidden even if you receive new messages in them.'
      },
      {
        question: 'What is "Last Seen"?',
        answer: 'Hiding your last seen prevents other users from knowing the last time you were active — your way of staying private.'
      },
      {
        question: 'What is "User Busy Status"?',
        answer: 'This sets an automatic reply for when you\'re busy so people know you\'re not available to chat right now.'
      },
      {
        question: 'What does "Auto Download" mean?',
        answer: 'Allows photos, videos, and files sent to you to download automatically so you can view them instantly.'
      },
      {
        question: 'What happens if I "Clear All Conversation"?',
        answer: 'It deletes all your chat history on your device. You\'ll start fresh with no old messages.'
      },
      {
        question: 'What is "Starred Messages"?',
        answer: 'Use this option to star important messages in chats for future reference.'
      },
      {
        question: 'How do I provide "App Feedback"?',
        answer: 'Use the attached Google form to fill in details such as your username and the problem you faced within our app.'
      },
      {
        question: 'How do I "Report a Problem"?',
        answer: 'Choose this option to let us know if you spot a bug or something isn\'t working. Select the issue type, describe it, and hit Submit.'
      },
      {
        question: 'What happens if I "Logout"?',
        answer: 'You\'ll be signed out of your account on this device. You can log back in anytime.'
      },
      {
        question: 'What does "Delete Account" do?',
        answer: 'This permanently removes your account and all your data from our system. Make sure you really want to do this before you proceed.'
      },
      {
        question: 'How do I edit my profile information?',
        answer: 'Go to your profile and tap "Edit Profile."'
      },
      {
        question: 'Can I change my username or handle?',
        answer: 'No, you can\'t change your username once the account is created.'
      },
      {
        question: 'What happens when I make my account private?',
        answer: 'Only approved followers can see your posts and details.'
      },
      {
        question: 'Who can see my posts if my account is private?',
        answer: 'Only the people you accept as followers.'
      },
      {
        question: 'Can I make individual posts private while keeping my profile public?',
        answer: 'Yes. When you set a specific post to private, only you will be able to see it—even your followers won\'t have access to that post.'
      },
      {
        question: 'Can I deactivate my account?',
        answer: 'Currently, we do not offer a deactivation option. However, you can log out or delete your account. Logging out stops notifications but keeps your profile visible.'
      },
      {
        question: 'Can I hide my followers/following list?',
        answer: 'You cannot manually hide them. If your account is private, only approved followers can view these lists.'
      },
      {
        question: 'Who can comment on my posts?',
        answer: 'Public account: anyone can comment. Private account: only approved followers can comment.'
      },
      {
        question: 'Can I turn off comments on my posts?',
        answer: 'Yes, you can Disable & Turn off comments or Delete all comments and then Turn off comments.'
      },
      {
        question: 'How do I remove someone from my followers?',
        answer: 'Go to your followers list and tap "Remove" next to their name.'
      },
      {
        question: 'How do I see which accounts I\'ve blocked?',
        answer: 'Go to Privacy & Safety → Blocked Accounts.'
      },
      {
        question: 'How do I change my profile picture or bio?',
        answer: 'Tap your profile and select "Edit Profile."'
      },
      {
        question: 'How do I change my name, bio or email?',
        answer: 'Go to "Edit Profile" and update the information (username cannot be changed).' 
      },
      {
        question: 'Can I hide my posts from specific followers?',
        answer: 'Not directly, but you can remove them or block them. Private accounts hide posts from non-approved users.'
      },
      {
        question: 'What\'s the difference between a public and private account?',
        answer: 'Public: anyone can see your profile and posts. Private: only approved followers can see your content.'
      },
      {
        question: 'Can I preview what my profile looks like to others?',
        answer: 'Currently, there\'s no preview option. You can switch to another account to view your profile.'
      },
      {
        question: 'What happens when I change my profile picture or name?',
        answer: 'Your new picture or name is instantly updated for everyone.'
      },
      {
        question: 'What should I do if someone is pretending to be me?',
        answer: 'Report the fake profile by tapping the Flag symbol on their profile and choosing a reason.'
      },
      {
        question: 'Can I use special characters or emojis in my username or bio?',
        answer: 'Yes, but your username must be unique. Emojis are allowed in your bio.'
      },
      {
        question: 'Do I get notifications if someone saves my posts?',
        answer: 'Yes, you\'ll receive notifications when someone likes, comments, or saves your posts and you can view the counts.'
      },
      {
        question: 'Where can I see the posts I\'ve saved?',
        answer: 'Saved and liked posts are available in your profile under their respective sections.'
      },
      {
        question: 'How can I delete my account?',
        answer: 'In Privacy and Settings, go to Account Management. Click on Account Ownership and Control → Delete Account.'
      },
      {
        question: 'What does "Delete Account" do?',
        answer: 'Once you click on Delete Account, choose the reason for deletion. Your account will be scheduled for permanent deletion after 30 days.'
      },
      {
        question: 'What if I want to cancel my Delete Account request?',
        answer: 'You can cancel your request by opening the Vreels App and entering your number. You will be asked to confirm cancellation. Note: Once cancelled, you cannot delete your account again until 30 days.'
      },
      {
        question: 'How can I deactivate my account?',
        answer: 'In Privacy and Settings, go to Account Management. Click on Account Ownership and Control → Deactivate Account.'
      },
      {
        question: 'What does "Deactivate Account" do?',
        answer: 'Once you click on Deactivate Account, choose the reason. Your account will be deactivated and you will be logged out of the app.'
      },
      {
        question: 'What if I want to reactivate my account?',
        answer: 'You can reactivate your account by opening the Vreels App and entering your number. You will be asked to confirm reactivation. Note: Once reactivated, you cannot deactivate your account again until 14 days.'
      }
    ]
  },
  {
    module: 'Reels',
    faqs: [
      {
        question: 'What are Reels in this app?',
        answer: 'Reels are short-form videos you can create and share to express your creativity or tell your story.'
      },
      {
        question: 'Where can I view Reels?',
        answer: 'Explore Reels AI generated Reels based on your viewing pattern, popular reels and trending content\nFollowing Reels by people you follow\nMy Reels Reels you\'ve uploaded (both public and private\nPrivate you can see your private reels in your my profile section of private reels, only for your eyes'
      },
      {
        question: 'How do I create a Reel or Pix?',
        answer: (
          <>
            Tap the
            <img
              src="/images/faq/upload.png"
              alt="Upload icon"
              className="inline-block w-6 h-6 mx-1 align-text-bottom"
            />
            icon on any tab, then pick the
            <span className="inline-flex items-center justify-center w-8 h-8 bg-black rounded-full mx-1 align-text-bottom">
              <img
                src="/images/reels_icon.png"
                alt="Create Reel icon"
                className="w-4 h-4"
                style={{ filter: 'invert(48%) sepia(92%) saturate(2973%) hue-rotate(4deg) brightness(102%) contrast(103%)' }}
              />
            </span>
            icon to create a Reel—record or upload a video and post. To share an image, choose the
            <span className="inline-flex items-center justify-center w-8 h-8 bg-black rounded-full mx-1 align-text-bottom">
              <img
                src="/images/pix_icon.png"
                alt="Create Pix icon"
                className="w-4 h-4"
                style={{ filter: 'invert(48%) sepia(92%) saturate(2973%) hue-rotate(4deg) brightness(102%) contrast(103%)' }}
              />
            </span>
            icon and upload your pix or image.
          </>
        )
      },
      {
        question: 'Who can view my Reels?',
        answer: 'If your account is public, anyone can see your Reels. If it\'s private, only your followers can.'
      },
      {
        question: 'Can I like, comment, or share Reels?',
        answer: 'Yes, you can interact with any public Reel to like, comment and share.'
      },
      {
        question: 'Can I save a Reel to view later?',
        answer: 'Yes, use the bookmark icon to save Reels to your saved list. You can always see your bookmarked reels in your "my profile" section with the bookmark icon.'
      },
      {
        question: 'Can I see the reels which I liked?',
        answer: 'Yes, you can see the reels you liked in the profile section with the like icon.'
      },
      {
        question: 'Can I report inappropriate Reels?',
        answer: 'Yes. Tap the Flag icon on any Reel and choose "Report" with a reason.'
      },
      {
        question: 'Why is my Reel not getting views?',
        answer: 'Make sure your account is not private, and your Reel doesn\'t violate community guidelines.'
      },
      {
        question: 'Can I delete or edit my posted Reels?',
        answer: 'You can delete them or edit them by clicking on the settings icon on each reel.'
      },
      {
        question: 'Can I disable comments and delete comments if I don\'t like them?',
        answer: 'Yes, you can turn off comments on your reel by clicking on the settings icon on each reel.'
      },
      {
        question: 'How do I post a Reel as private (like a draft)?',
        answer: 'When posting a Reel, choose the Private visibility option. Only you can see it in the profile section under the private icon tab.'
      },
      {
        question: 'What are Private Reels?',
        answer: 'Private Reels are videos only visible to you. You can use them as drafts or personal memories.'
      },
      {
        question: 'Can I share a Private Reel later?',
        answer: 'Yes, edit the visibility of a Private Reel from Private to Public anytime.'
      },
      {
        question: 'Can anyone else see my Private Reels?',
        answer: 'No, they are only visible to you.'
      },
      {
        question: 'Why can\'t others see my Reel?',
        answer: 'Ensure your Reel is not marked private, and your account is not set to private (unless they follow you).'
      }
    ]
  },
  {
    module: 'Pix & Pouches',
    faqs: [
      {
        question: 'What is a Pix?',
        answer: 'Pix are individual image posts you can share with the community or keep in pouches.'
      },
      {
        question: 'Where can I explore Pix?',
        answer: 'Tap the Explore Pix tab to see all public Pix shared by users.'
      },
      {
        question: 'What is a Pouch?',
        answer: 'A Pouch is a visual collection of Pix or inspiration.'
      },
      {
        question: 'How do I create a Pouch?',
        answer: 'Go to the upload icon on any tab → tap Create Pouch → name it → choose visibility → start adding content.'
      },
      {
        question: 'Where can I see public Pouches?',
        answer: 'Go to the Explore Pouches tab to browse public Pouches from other users.'
      },
      {
        question: 'Can I make a Pouch private or public?',
        answer: 'Yes, during creation or editing, choose from Public or Private visibility options.'
      },
      {
        question: 'How do I delete or rename a Pouch?',
        answer: 'Go to the Pouch → tap the setting icon → select Edit or Delete.'
      },
      {
        question: 'Can I like, comment or share a Pouch?',
        answer: 'Yes, you can interact with any Pouch that is visible to you.'
      },
      {
        question: 'Can I save a Pouch to view later?',
        answer: 'Yes, use the bookmark icon to save Pouches to your saved list in your profile.'
      },
      {
        question: 'Can I see the Pouches which I liked?',
        answer: 'Yes, you can see the Pouches you liked in the profile section with the like icon.'
      },
      {
        question: 'Can I report an inappropriate Pouch?',
        answer: 'Yes. Tap the Flag icon on any Pouch and choose "Report" with a reason.'
      },
      {
        question: 'What roles exist in a pouch?',
        answer: 'There are two roles for collaborators: Admin and Editor.'
      },
      {
        question: 'What about the owner?',
        answer: 'The Owner is not a separate role in the system. When someone creates a pouch, they are automatically added as an Admin. Unlike other Admins, this creator Admin cannot be removed or demoted. In the interface, they will simply appear as an Admin.'
      },
      {
        question: 'What can Admins do?',
        answer: 'Add or remove collaborators, promote or demote Editors, remove others\' posts/images, delete others\' comments, and do everything an Editor can (add posts, comment, delete own comments).'
      },
      {
        question: 'What can Editors do?',
        answer: 'Add images/posts, add comments, delete their own comments. ❌ Cannot add/remove collaborators. ❌ Cannot remove others\' posts or comments.'
      },
      {
        question: 'Can there be more than one Admin or Editor?',
        answer: 'Yes. A pouch can have multiple Admins and multiple Editors at the same time.'
      },
      {
        question: 'Can Admins change other collaborators\' permissions?',
        answer: 'Yes. Admins can turn individual permissions on or off for other collaborators, depending on what actions they should have.'
      },
      {
        question: 'Is there always at least one Admin?',
        answer: 'Yes. The creator Admin is permanent and cannot be removed. This ensures that every pouch always has at least one Admin.'
      },
      {
        question: 'Can roles be changed later?',
        answer: 'Right now: once a collaborator is set as Admin or Editor, their role cannot be changed. Future update: role changes (Editor → Admin, Admin → Editor) will be supported.'
      },
      {
        question: 'Are there limits on posts?',
        answer: 'No. There is no limit on how many posts a pouch can contain.'
      },
      {
        question: 'Can an Editor be promoted to Admin?',
        answer: 'Not yet. This feature is planned for a future update. For now, once someone is added as an Editor, they stay an Editor.'
      },
      {
        question: 'Can the pouch creator leave the pouch?',
        answer: 'No. The pouch creator is a special Admin and cannot leave, be removed, or be downgraded.'
      },
      {
        question: 'Do collaborators get a notification when added?',
        answer: 'Yes. When someone is added as a collaborator, they receive a push notification and an in-app notification.'
      },
      {
        question: 'Can Editors delete the pouch?',
        answer: 'No. Only the creator Admin has full ownership, and even other Admins cannot delete the pouch.'
      },
      {
        question: 'Can Admins remove the creator Admin?',
        answer: 'No. The creator Admin is permanent and cannot be removed or downgraded by anyone.'
      },
      {
        question: 'Can multiple Admins remove each other?',
        answer: 'Yes. Admins can remove other Admins (except the creator Admin).'
      },
      {
        question: 'Can collaborators be added to multiple pouches?',
        answer: 'Yes. The same user can be an Admin in one pouch and an Editor in another.'
      },
      {
        question: 'Do collaborators see who else is in the pouch?',
        answer: 'Yes. All collaborators can see the list of Admins and Editors in the pouch.'
      },
      {
        question: 'Can an Editor remove themselves from a pouch?',
        answer: 'Yes. Editors can leave the pouch voluntarily if they no longer want to collaborate.'
      },
      {
        question: 'Can an Admin remove themselves from a pouch?',
        answer: 'Yes, but not if they are the last remaining Admin. There must always be at least one Admin in the pouch.'
      },
      {
        question: 'What happens if an Admin accidentally removes all other Admins?',
        answer: 'The pouch will still have the creator Admin, since they can never be removed.'
      },
      {
        question: 'Will more roles be added in the future?',
        answer: 'Yes. Future updates may include additional roles like Viewer (read-only) to give more flexibility.'
      },
      {
        question: 'Can two Admins assign different permissions to the same Editor?',
        answer: 'No. Editor permissions are fixed and cannot be changed. All Editors always have the same set of actions (add posts, comment, delete their own comments). Only Admin permissions are flexible and can be turned on/off by other Admins.'
      }
    ]
  },
  {
    module: 'Heat Map',
    faqs: [
      {
        question: 'What is the Heat Map feature in VREELS?',
        answer: 'The Heat Map gives you a live, interactive view of where your friends are most active.'
      },
      {
        question: 'How does the Heat Map work?',
        answer: 'Under \'My Profile\' section, click the location icon in the top bar next to the bell icon. Select the appropriate location privacy controls \'Deny\', \'Allow Once\', or \'Allow Always\' and the map appears. The map highlights areas where friends are engaging. The more activity in a location, the "hotter" it appears—shown with brighter colors and stronger intensity. This lets you instantly spot lively places both near you and worldwide.'
      },
      {
        question: 'Does the Heat Map share my location?',
        answer: 'Your location is only shared if you choose to allow it. You can select \'Deny\', \'Allow Once\', or \'Allow Always\'. Location updates only when you actively use the Heat Map, and never run in the background.'
      },
      {
        question: 'Can I filter the Heat Map?',
        answer: 'Yes! Use the Nearby Friends option in the top-right corner of the map to see which of your friends are active within a chosen radius. You can also switch views between global activity and friend-only activity for more control.'
      },
      {
        question: 'Will the Heat Map drain my phone\'s battery or data?',
        answer: 'Not at all. The Heat Map is optimized to refresh only when you\'re using it—so there\'s no background drain. Data and battery usage remain minimal.'
      },
      {
        question: 'Can I hide my activity from appearing on the Heat Map?',
        answer: 'Absolutely. With built-in Privacy Controls, you decide whether your activity contributes to the Heat Map. You can set it to \'Deny\', \'Allow Once\', or \'Allow Always\' depending on your comfort level.'
      },
      {
        question: 'Can others see my exact location on the Heat Map?',
        answer: 'No. For privacy, your exact location is never shown. Instead, the Heat Map aggregates activity into zones, so others can see general hotspots without pinpointing individuals.'
      }
    ]
  },
  {
    module: 'Memory Capsule',
    faqs: [
      {
        question: 'What is a Capsule?',
        answer: 'A Capsule is like a digital time-locked memory. You can create one with photos, videos, and set it to unlock at a specific time (like a birthday).'
      },
      {
        question: 'How do I create a Capsule?',
        answer: 'In the app, go to profile section and click on capsule icon. Click on \'+\' icon and start creating capsule. Add a title, description. Set the unlock date/time. Optionally invite contributors to add media.'
      },
      {
        question: 'Who can add content to my Capsule?',
        answer: 'You can invite contributors/coadmins (friends/family) who can upload photos, videos, before it unlocks.'
      },
      {
        question: 'How to give/invite people to my Capsule?',
        answer: 'In the capsule section, click on the Access icon and give access to contributors or co-admin to add the content.'
      },
      {
        question: 'Where can I add the content in the Capsule?',
        answer: 'In the Capsule section, click on the media icon and start uploading the content.'
      },
      {
        question: 'Who can view my Capsule?',
        answer: 'You decide who gets access: Add viewers who will see the Capsule once it unlocks.'
      },
      {
        question: 'What happens before a Capsule unlocks?',
        answer: 'Before unlock: Only the creator and coadmin sees all uploads. Viewers will directly receive a notification when unlocked.'
      },
      {
        question: 'What happens when a Capsule unlocks?',
        answer: 'Once the unlock condition is met: Everyone with access can view all media inside in the "other capsules" section. You can view your capsule in the "My Capsules" section.'
      },
      {
        question: 'Can I remove or change media after uploading?',
        answer: 'Yes — the creator can remove any media before unlocking. Contributors/co-admin can remove only their media. After unlocking, content becomes permanent in the timeline unless the creator deletes it.'
      },
      {
        question: 'How will I know when a Capsule unlocks?',
        answer: 'You\'ll receive a notification when: A Capsule you\'re part of unlocks. You can also check unlock status anytime in the app if you are contributor or co-admin.'
      },
      {
        question: 'Can I delete a Capsule?',
        answer: 'Yes, the creator can delete a Capsule before or after unlocking. This will remove all its media, contributors, and notifications.'
      },
      {
        question: 'Can I save a Capsule?',
        answer: 'Yes, the Creator or Viewer can save the capsule after the unlock. If you don\'t save it the capsule will be deleted 7 days after it is unlocked.'
      }
    ]
  },
  // {
  //   module: 'Shopping & Payments',
  //   faqs: [
  //     {
  //       question: 'Which payment methods are supported?',
  //       answer: 'We currently accept all major credit cards and Apple Pay / Google Pay in supported regions.'
  //     },
  //     {
  //       question: 'How do I request a refund?',
  //       answer: 'Go to Orders → tap the order → Request Refund and provide a brief reason. Our team will review within 48 hours.'
  //     }
  //   ]
  // },
  {
    module: 'Chats',
    faqs: [
      { question: 'What happens when I send a message to someone?', answer: 'They get your message right away if they are online. If not, they will see it when they come back.' },
      { question: 'How do I know if my message was delivered or not?', answer: 'You\'ll see a gray dot when the message is sent, an orange dot when it\'s delivered, and a green dot when the person has seen it.' },
      { question: 'Can I delete a message I sent?', answer: 'Yes, you can delete a message you\'ve sent. You have two options: "Delete for Me", which removes it only from your view, or "Delete for Both", which deletes the message for both you and the recipient.' },
      { question: 'Will the other person know if I delete a message?', answer: 'They may see a note that says the message was deleted.' },
      { question: 'Can I unsend a message before it\'s read?', answer: 'Yes, you can delete the message anytime, even before they read it.' },
      { question: 'Can I message someone who doesn\'t follow me?', answer: 'Yes, you can message anyone who registered with Vreels.' },
      { question: 'What happens when I block someone in chat?', answer: 'They can no longer message you or see your profile.' },
      { question: 'Will I see old messages if I unblock a contact?', answer: 'Yes, old messages will still be there unless you delete it.' },
      { question: 'Can I report abusive or spammy messages?', answer: 'Yes, just tap on the message or profile and choose "Report".' },
      { question: 'Why can\'t I send messages to some people?', answer: 'They may have blocked you. That\'s why you can\'t see the person\'s profile to send the message.' },
      { question: 'Can I pin or favorite certain chats?', answer: 'Yes, you can pin chats so they stay at the top.' },
      { question: 'Do chats disappear after a certain time?', answer: 'No, unless you delete them.' },
      { question: 'Are messages encrypted or private?', answer: 'Yes, your messages are private and secure.' },
      { question: 'Will I be notified when someone is typing?', answer: 'Yes, you will see a "typing..." message.' },
      { question: 'Can I send images or videos in chat?', answer: 'Yes, you can send photos, videos, and more.' },
      { question: 'Are there limits on media file size in chat?', answer: 'Yes, large files might not be uploaded. Try to keep the files small.' },
      { question: 'Can I search for specific messages in a chat?', answer: 'Yes, use the search bar inside the chat.' },
      { question: 'How do I mute a chat or stop notifications from someone?', answer: 'You can turn off the push notifications for chats module from privacy and settings of your profile.' },
      { question: 'Is there a limit to the number of participants in group audio and video calls?', answer: 'Yes, a maximum of 8 participants can join a group audio or video call at a time.' },
      { question: 'Do chats sync across all my devices?', answer: 'Yes, if you\'re logged into the same account, your chats are synced.' },
      { question: 'What happens to my chats if I uninstall the app?', answer: 'Your chats are stored securely and will reappear when you reinstall and log in back to the same account again unless you delete the account.' },
      { question: 'Are deleted chats or messages recoverable?', answer: 'No, once you delete a message or chat, it cannot be recovered.' },
      { question: 'Is there a separate settings section for Chats in your app?', answer: 'Yes, our app includes a dedicated Chat Settings section within the Privacy & Settings menu, allowing you to manage and customize all aspects of the chat module.' },
      { question: 'Can I reply to a specific message in chat?', answer: 'Yes, long-press the message and tap "Reply" to quote it. You can reply, forward, copy and delete a specific message as you want.' },
      { question: 'Can I see if someone is online or last active?', answer: 'Yes, if they have allowed it in their settings, you\'ll see their last seen time. You can also hide your own activity status.' }
    ]
  },
  {
    module: "Other Users' Profiles",
    faqs: [
      { question: 'Why can\'t I see someone\'s posts?', answer: 'Their profile may be private, or they blocked you.' },
      { question: 'What does it mean when a profile is private?', answer: 'You need to follow and get accepted to see their posts.' },
      { question: 'How do I send a follow request?', answer: 'Tap the "Follow" button. If they\'re private, they\'ll need to accept.' },
      { question: 'Can I cancel a follow request I sent?', answer: 'Yes. Tap the "Requested" button again to cancel the follow request.' },
      { question: 'What does "Follow Back" mean?', answer: 'They follow you, but you don\'t follow them. You can follow them back.' },
      { question: 'How do I know if someone follows me?', answer: 'Check your followers list or their profile.' },
      { question: 'Can I follow someone without them knowing?', answer: 'No, they will receive a notification.' },
      { question: 'What happens when I block someone?', answer: 'They can\'t see or interact with you anymore.' },
      { question: 'Will someone know I blocked them?', answer: 'No direct alert, but they may notice they can\'t find you.' },
      { question: 'What happens when someone blocks me?', answer: 'You won\'t be able to find or contact them.' },
      { question: 'Can I still see posts if someone blocks me?', answer: 'No, their posts and profile will be hidden.' },
      { question: 'Can I message someone from their profile?', answer: 'Yes, if they allow messages from users like you.' },
      { question: 'Can I see someone\'s liked posts or followers?', answer: 'You can\'t see their liked posts, but you can see their followers list.' },
      { question: 'Can I interact with posts from private profiles?', answer: 'Only if they accept your follow request.' },
      { question: 'What happens if I follow someone and they block me later?', answer: 'You will be removed from their followers and can\'t view their profile.' },
      { question: 'Why does a profile say "User not found"?', answer: 'They may have deleted their account or blocked you.' },
      { question: 'How do I share someone\'s profile with others?', answer: 'Use the share button on their profile to copy the link or send it.' },
      { question: 'How do I know if someone accepted my follow request?', answer: 'You\'ll get a notification and start seeing their posts.' },
      { question: 'Why did someone\'s posts suddenly disappear?', answer: 'They may have switched to a private account, blocked you, or deleted their posts.' }
    ]
  }
];

export default function FAQ({ isModal = false }) {
  const isMobile = window.innerWidth < 768; // hide back button on mobile
  // Track which module accordion is open
  const [expandedModule, setExpandedModule] = useState(null);
  // Track which question inside a module is open: { [moduleIndex]: questionIndex }
  const [expandedQuestion, setExpandedQuestion] = useState({});
  const [searchTerm, setSearchTerm] = useState('');

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToTop();
  }, []);

  const handleToggleModule = (idx) => {
    setExpandedModule(expandedModule === idx ? null : idx);
  };

  const handleToggleQuestion = (moduleIdx, questionIdx) => {
    setExpandedQuestion((prev) => ({
      ...prev,
      [moduleIdx]: prev[moduleIdx] === questionIdx ? null : questionIdx
    }));
  };

  const term = searchTerm.trim().toLowerCase();
  const words = term.split(/\s+/).filter(Boolean);

  const matchesText = (text) =>
    words.every((w) => text.includes(w));

  const filteredModules = term
    ? faqModules
        .map((module) => ({
          ...module,
          faqs: module.faqs.filter((faq) => {
            const q = faq.question.toLowerCase();
            const a = typeof faq.answer === 'string' ? faq.answer.toLowerCase() : '';
            return matchesText(q) || matchesText(a);
          }),
        }))
        .filter((m) => m.faqs.length > 0)
    : faqModules;

  const highlight = (text) => {
    if (!term) return text;
    const regex = new RegExp(`(${words.join('|')})`, 'gi');
    return text.split(regex).map((part, idx) =>
      regex.test(part) ? (
        <mark key={idx} className="bg-yellow-300 p-0.5 rounded">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-primary/5 py-12">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            {!isModal && !isMobile && (
              <Button
                onClick={() => window.history.back()}
                variant="outline"
                className="mb-6 group hover:bg-primary/10"
              >
                <ArrowLeft className="mr-2 h-4 w-4 group-hover:transform group-hover:-translate-x-1 transition-transform" />
                Back
              </Button>
            )}
            <h1 className="text-4xl md:text-5xl font-bold gradient-text-brand mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Find quick answers about each module of VREELS.
            </p>
          </motion.div>

          {/* Search Bar */}
          <div className="max-w-md mx-auto mb-8">
            <Input
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full"
            />
          </div>

          {/* Modules Accordion */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <Card>
              <CardContent className="p-0 divide-y divide-border">
                {filteredModules.length === 0 && (
                  <p className="p-6 text-center text-muted-foreground">No FAQs match your search.</p>
                )}
                {filteredModules.map((module, moduleIdx) => (
                  <div key={module.module}>
                    {/* Module Header */}
                    <button
                      onClick={() => !term && handleToggleModule(moduleIdx)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-secondary/50 transition-colors duration-200"
                    >
                      <span className="font-semibold text-lg">
                        {module.module}
                      </span>
                      {(term || expandedModule === moduleIdx) ? (
                        <ChevronUp className="h-5 w-5 text-muted-foreground" />
                      ) : (
                        <ChevronDown className="h-5 w-5 text-muted-foreground" />
                      )}
                    </button>

                    {/* Questions within Module */}
                    {(term || expandedModule === moduleIdx) && (
                      <div className="bg-background/70">
                        {module.faqs.map((faq, qIdx) => (
                          <div key={qIdx} className="border-t border-border first:border-t-0">
                            <button
                              onClick={() => handleToggleQuestion(moduleIdx, qIdx)}
                              className="w-full px-8 py-4 flex items-center justify-between hover:bg-secondary/40 transition-colors duration-200"
                            >
                              <span>{highlight(faq.question)}</span>
                              {(term || expandedQuestion[moduleIdx] === qIdx) ? (
                                <ChevronUp className="h-5 w-5 text-muted-foreground" />
                              ) : (
                                <ChevronDown className="h-5 w-5 text-muted-foreground" />
                              )}
                            </button>
                            {(term || expandedQuestion[moduleIdx] === qIdx) && (
                              <div className="px-8 pb-4 text-muted-foreground">
                                {typeof faq.answer === 'string' ? highlight(faq.answer) : faq.answer}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          </motion.div>

          {/* Back to Top Button */}
          <div className="text-center pt-8">
            <Button
              onClick={scrollToTop}
              className="btn-gradient-orange"
            >
              Back to Top
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
} 